import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import FingerprintScanner from '../components/FingerprintScanner';
import useBiometric from '../hooks/useBiometric';
import { Fingerprint, ChevronDown, ChevronUp } from 'lucide-react';
import './Users.css';
import './MyAttendance.css';

const AttendanceManagement = () => {
  useMarkAsReadOnMount(['attendance']);

  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [activeEmployeeId, setActiveEmployeeId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [nowTs, setNowTs] = useState(Date.now());
  const [workType, setWorkType] = useState('all');
  const [exportPeriod, setExportPeriod] = useState('monthly');
  const calendarRef = useRef(null);
  const [fpStationOpen, setFpStationOpen] = useState(false);
  const [fpLastScan, setFpLastScan] = useState(null);
  const [fpClockStatus, setFpClockStatus] = useState('ready');
  const [fpClockError, setFpClockError] = useState(null);
  const { deviceStatus, config } = useBiometric();

  const normalizeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (Array.isArray(value?.records)) return value.records;
    if (Array.isArray(value?.items)) return value.items;
    if (Array.isArray(value?.attendance)) return value.attendance;
    return [];
  };

  const formatDateShort = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const parseTimeToMinutes = (value) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    return (hours * 60) + minutes;
  };

  const isRemoteRecord = (record) => {
    const notes = (record?.notes || '').toLowerCase();
    return Boolean(record?.isRemote || record?.method === 'remote' || notes.includes('remote'));
  };

  const isLeaveRecord = (record) => {
    const status = String(record?.status || '').toLowerCase();
    const notes = String(record?.notes || '').toLowerCase();
    return status === 'leave' || notes.includes('leave');
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersResponse, recordsResponse] = await Promise.all([api.get('/users'), api.get('/attendance')]);
      const users = normalizeArray(usersResponse);
      const regularRecords = normalizeArray(recordsResponse);

      const remoteHistoryResults = await Promise.allSettled(
        users.map((u) => api.get(`/attendance/remote/history/${u.id}`)),
      );

      const remoteRecords = remoteHistoryResults.flatMap((result, index) => {
        if (result.status !== 'fulfilled') return [];
        const payload = result.value;
        const history = Array.isArray(payload) ? payload : (Array.isArray(payload?.history) ? payload.history : []);
        return history.map((record) => ({
          id: `remote-${record.id}`,
          userId: record.userId || users[index]?.id,
          date: String(record.date || '').slice(0, 10),
          clockIn: record.clockIn,
          clockOut: record.clockOut,
          status: ['active', 'completed', 'auto_clocked_out'].includes(record.status) ? 'present' : (record.status || 'present'),
          notes: `Method: remote, Session: ${record.sessionId || '-'}`,
          isRemote: true,
        }));
      });

      setEmployees(users);
      setAttendance([...regularRecords, ...remoteRecords]);
    } catch (error) {
      console.error('Error loading attendance management data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, 30000);
    return () => clearInterval(timer);
  }, [fetchData]);

  useEffect(() => {
    const timer = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getWorkedDuration = (record) => {
    if (!record?.clockIn) return '-';

    const inTotal = parseTimeToMinutes(record.clockIn);
    if (inTotal === null) return '-';

    let totalMinutes;
    if (record.clockOut) {
      const outTotal = parseTimeToMinutes(record.clockOut);
      if (outTotal === null) return '-';
      totalMinutes = outTotal - inTotal;
    } else {
      const now = new Date(nowTs);
      totalMinutes = (now.getHours() * 60 + now.getMinutes()) - inTotal;
    }

    if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '-';

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const duration = `${hours}h ${minutes}m`;

    return record.clockOut ? duration : `${duration} (In Progress)`;
  };

  const getTotalWorkedDurationForDate = (recordsForDay) => {
    if (!recordsForDay.length) return '-';

    let totalMinutes = 0;
    recordsForDay.forEach((record) => {
      if (!record.clockIn || !record.clockOut) return;
      const inTotal = parseTimeToMinutes(record.clockIn);
      const outTotal = parseTimeToMinutes(record.clockOut);
      if (inTotal === null || outTotal === null) return;
      const worked = outTotal - inTotal;
      if (worked > 0) totalMinutes += worked;
    });

    if (!totalMinutes) return '-';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getWorkedMinutes = (record) => {
    if (!record?.clockIn || !record?.clockOut) return 0;
    const inTotal = parseTimeToMinutes(record.clockIn);
    const outTotal = parseTimeToMinutes(record.clockOut);
    if (inTotal === null || outTotal === null) return 0;
    const worked = outTotal - inTotal;
    return worked > 0 ? worked : 0;
  };

  const formatMinutesToHours = (minutes) => {
    const safeMinutes = Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    const hours = Math.floor(safeMinutes / 60);
    const remMinutes = safeMinutes % 60;
    return `${hours}h ${remMinutes}m`;
  };

  const getMonthlyWorkingDaysCount = () => {
    const today = new Date();
    const isCurrentMonth = today.getFullYear() === currentYear && (today.getMonth() + 1) === currentMonth;
    const lastDay = isCurrentMonth ? today.getDate() : new Date(currentYear, currentMonth, 0).getDate();
    let workingDays = 0;

    for (let day = 1; day <= lastDay; day += 1) {
      const dow = new Date(currentYear, currentMonth - 1, day).getDay();
      if (dow !== 0 && dow !== 6) {
        workingDays += 1;
      }
    }

    return workingDays || 1;
  };

  const filteredEmployees = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((u) =>
      `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q),
    );
  }, [employees, search]);

  const employeeRecords = useMemo(() => {
    if (!activeEmployeeId) return [];
    return attendance
      .filter((r) => String(r.userId) === String(activeEmployeeId))
      .filter((r) => workType === 'all' ? true : (workType === 'remote' ? isRemoteRecord(r) : !isRemoteRecord(r)));
  }, [attendance, activeEmployeeId, workType]);

  const employeeCalendarRecords = useMemo(() => {
    return employeeRecords.filter((r) => {
      if (!r?.date) return false;
      const d = new Date(r.date);
      return d.getMonth() + 1 === currentMonth && d.getFullYear() === currentYear;
    });
  }, [employeeRecords, currentMonth, currentYear]);

  const toggleEmployee = (id) => {
    setSelectedEmployees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = (checked) => {
    if (!checked) {
      setSelectedEmployees([]);
      return;
    }
    setSelectedEmployees(filteredEmployees.map((u) => u.id));
  };

  const applyPeriodFilter = (rows) => {
    const today = new Date().toISOString().slice(0, 10);
    if (exportPeriod === 'daily') {
      return rows.filter((r) => r?.date === today);
    }
    return rows.filter((r) => {
      if (!r?.date) return false;
      const d = new Date(r.date);
      return d.getMonth() + 1 === currentMonth && d.getFullYear() === currentYear;
    });
  };

  const exportXlsx = async (rows, filename, options = {}) => {
    const { includeAllEmployees = false } = options;
    const usersById = new Map(employees.map((u) => [String(u.id), u]));
    const headerRowIndex = 4;

    const getStatusStyle = (status) => {
      const value = String(status || '').toLowerCase();
      const colorMap = {
        present: 'FF2E7D32',
        late: 'FFEF6C00',
        leave: 'FF1565C0',
        absent: 'FFC62828',
      };
      return {
        font: { bold: true, color: { rgb: colorMap[value] || 'FF374151' } },
      };
    };

    const getMonthlyRowFill = (percentage) => {
      if (percentage >= 90) return 'FFE8F5E9';
      if (percentage >= 50) return 'FFFFF8E1';
      return 'FFFFEBEE';
    };

    const getDailyRowFill = (workedMinutes) => {
      const quotaMinutes = 7 * 60; // 10:00 to 17:00
      const partialQuotaMinutes = 4 * 60;
      if (workedMinutes >= quotaMinutes) return 'FFE8F5E9';
      if (workedMinutes >= partialQuotaMinutes) return 'FFFFF8E1';
      return 'FFFFEBEE';
    };

    const applyRowFill = (row, fillColor) => {
      row.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: fillColor },
        };
      });
    };

    const workbook = new ExcelJS.Workbook();

    if (exportPeriod === 'monthly') {
      const worksheet = workbook.addWorksheet('Monthly Summary');
      const workingDays = getMonthlyWorkingDaysCount();
      const byEmployee = new Map();

      if (includeAllEmployees) {
        employees.forEach((u) => {
          byEmployee.set(String(u.id), {
            userId: u.id,
            workedDates: new Set(),
            totalMinutes: 0,
            remoteMinutes: 0,
            onsiteMinutes: 0,
          });
        });
      }

      rows.forEach((r) => {
        const key = String(r.userId);
        if (!byEmployee.has(key)) {
          byEmployee.set(key, {
            userId: r.userId,
            workedDates: new Set(),
            totalMinutes: 0,
            remoteMinutes: 0,
            onsiteMinutes: 0,
          });
        }

        const bucket = byEmployee.get(key);
        const workedMinutes = getWorkedMinutes(r);
        if (workedMinutes <= 0) return;

        bucket.totalMinutes += workedMinutes;
        bucket.workedDates.add(r.date);

        if (isRemoteRecord(r)) {
          bucket.remoteMinutes += workedMinutes;
        } else if (!isLeaveRecord(r)) {
          bucket.onsiteMinutes += workedMinutes;
        }
      });

      const summaryRows = Array.from(byEmployee.values())
        .sort((a, b) => String(a.userId).localeCompare(String(b.userId)))
        .map((bucket) => {
          const user = usersById.get(String(bucket.userId));
          const workedDays = bucket.workedDates.size;
          const attendancePercentageValue = Number(((workedDays / workingDays) * 100).toFixed(1));

          return {
            Employee: user?.username || '-',
            'Worked Days': workedDays,
            'Worked Hours': formatMinutesToHours(bucket.totalMinutes),
            'Remote Hours': formatMinutesToHours(bucket.remoteMinutes),
            'On Site Hours': formatMinutesToHours(bucket.onsiteMinutes),
            'Attendance Percentage': `${attendancePercentageValue}%`,
            __attendancePercentageValue: attendancePercentageValue,
          };
        });

      worksheet.addRow(['Monthly Attendance Summary']);
      worksheet.addRow([`Period: ${String(currentMonth).padStart(2, '0')}/${currentYear}`]);
      worksheet.addRow([`Generated at: ${new Date().toLocaleString('en-GB')}`]);

      const headers = ['Employee', 'Worked Days', 'Worked Hours', 'Remote Hours', 'On Site Hours', 'Attendance Percentage'];
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true };

      summaryRows.forEach((row) => {
        const dataRow = worksheet.addRow([
          row.Employee,
          row['Worked Days'],
          row['Worked Hours'],
          row['Remote Hours'],
          row['On Site Hours'],
          row['Attendance Percentage'],
        ]);
        applyRowFill(dataRow, getMonthlyRowFill(row.__attendancePercentageValue));
      });

      worksheet.columns = [
        { width: 20 },
        { width: 12 },
        { width: 14 },
        { width: 14 },
        { width: 14 },
        { width: 22 },
      ];

      worksheet.autoFilter = {
        from: { row: headerRowIndex, column: 1 },
        to: { row: headerRowIndex, column: 6 },
      };

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), filename);
      return;
    }

    let exportRows = [...rows];

    if (exportPeriod === 'daily' && includeAllEmployees) {
      const presentUserIds = new Set(exportRows.map((r) => String(r.userId)));
      const placeholderRows = employees
        .filter((u) => !presentUserIds.has(String(u.id)))
        .map((u) => ({
          id: `placeholder-${u.id}`,
          userId: u.id,
          clockIn: null,
          clockOut: null,
          status: 'absent',
          notes: '-',
          isRemote: false,
        }));
      exportRows = [...exportRows, ...placeholderRows];
    }

    const normalizedRows = exportRows
      .sort((a, b) => {
        if (String(a.userId) !== String(b.userId)) {
          return String(a.userId).localeCompare(String(b.userId));
        }
        return String(a.date).localeCompare(String(b.date));
      })
      .map((r) => {
        const user = usersById.get(String(r.userId));
        const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '-';
        const status = String(r.status || '-').toUpperCase();
        const workedMinutes = getWorkedMinutes(r);
        const isRemote = isRemoteRecord(r);

        if (exportPeriod === 'daily') {
          return {
            'Employee ID': r.userId,
            Employee: user?.username || fullName,
            'Clock In': r.clockIn || '-',
            'Clock Out': r.clockOut || '-',
            'Worked Hours': formatMinutesToHours(workedMinutes),
            'Worked Hours Remote': formatMinutesToHours(isRemote ? workedMinutes : 0),
            'Worked Hours Onsite': formatMinutesToHours(!isRemote && !isLeaveRecord(r) ? workedMinutes : 0),
            Status: status,
            Notes: r.notes || '-',
            __workedMinutes: workedMinutes,
            __statusValue: status,
          };
        }

        return {
          'Employee ID': r.userId,
          Employee: fullName,
          Email: user?.email || '-',
          Date: formatDateShort(r.date),
          'Clock In': r.clockIn || '-',
          'Clock Out': r.clockOut || '-',
          'Worked Hours': getWorkedDuration(r),
          Status: status,
          Type: isLeaveRecord(r) ? 'Leave' : (isRemoteRecord(r) ? 'Remote Work' : 'Regular Work'),
          Notes: r.notes || '-',
          __statusValue: status,
        };
      });

    const worksheet = workbook.addWorksheet('Attendance');
    worksheet.addRow(['Attendance Export']);
    worksheet.addRow([`Generated at: ${new Date().toLocaleString('en-GB')}`]);
    worksheet.addRow([]);

    const headers = exportPeriod === 'daily'
      ? ['Employee ID', 'Employee', 'Clock In', 'Clock Out', 'Worked Hours', 'Worked Hours Remote', 'Worked Hours Onsite', 'Status', 'Notes']
      : ['Employee ID', 'Employee', 'Email', 'Date', 'Clock In', 'Clock Out', 'Worked Hours', 'Status', 'Type', 'Notes'];

    const headerRow = worksheet.addRow(headers);
    headerRow.font = { bold: true };

    normalizedRows.forEach((row, index) => {
      const dataRow = worksheet.addRow(
        exportPeriod === 'daily'
          ? [
              row['Employee ID'],
              row.Employee,
              row['Clock In'],
              row['Clock Out'],
              row['Worked Hours'],
              row['Worked Hours Remote'],
              row['Worked Hours Onsite'],
              row.Status,
              row.Notes,
            ]
          : [
              row['Employee ID'],
              row.Employee,
              row.Email,
              row.Date,
              row['Clock In'],
              row['Clock Out'],
              row['Worked Hours'],
              row.Status,
              row.Type,
              row.Notes,
            ],
      );

      if (exportPeriod === 'daily') {
        applyRowFill(dataRow, getDailyRowFill(row.__workedMinutes || 0));
      }

      const statusColIndex = 8;
      const statusCell = dataRow.getCell(statusColIndex);
      const statusStyle = getStatusStyle(row.__statusValue);
      statusCell.font = statusStyle.font;
    });

    worksheet.columns = [
      ...(exportPeriod === 'daily'
        ? [
            { width: 12 },
            { width: 20 },
            { width: 10 },
            { width: 10 },
            { width: 14 },
            { width: 18 },
            { width: 18 },
            { width: 10 },
            { width: 45 },
          ]
        : [
            { width: 12 },
            { width: 24 },
            { width: 30 },
            { width: 12 },
            { width: 10 },
            { width: 10 },
            { width: 14 },
            { width: 10 },
            { width: 14 },
            { width: 45 },
          ]),
    ];

    worksheet.autoFilter = {
      from: { row: headerRowIndex, column: 1 },
      to: { row: headerRowIndex, column: exportPeriod === 'daily' ? 9 : 10 },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), filename);
  };

  const exportAll = () => {
    const rows = applyPeriodFilter(attendance);
    const periodLabel = exportPeriod === 'daily' ? 'daily' : `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
    exportXlsx(rows, `attendance-all-${periodLabel}.xlsx`, { includeAllEmployees: true });
  };

  const exportSelected = () => {
    if (!selectedEmployees.length) {
      alert('Please select at least one employee.');
      return;
    }
    const rows = applyPeriodFilter(attendance.filter((r) => selectedEmployees.includes(r.userId)));
    const periodLabel = exportPeriod === 'daily' ? 'daily' : `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
    exportXlsx(rows, `attendance-selected-${periodLabel}.xlsx`);
  };

  const handleFingerprintClock = async () => {
    setFpClockStatus('scanning');
    setFpClockError(null);
    try {
      // TODO: Replace null with actual template from device SDK
      // e.g.: const { templateData } = await deviceSdk.capture();
      const templateData = null;
      if (!templateData) {
        throw new Error('DEVICE_NOT_CONNECTED: Connect the fingerprint reader and implement the SDK capture.');
      }
      const res = await api.post('/attendance/fingerprint-clock', { templateData });
      const data = res?.data ?? res;
      setFpLastScan(data);
      setFpClockStatus('success');
      await fetchData();
      setTimeout(() => setFpClockStatus('ready'), 3000);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Clock failed';
      setFpClockError(msg);
      setFpClockStatus('error');
      setTimeout(() => setFpClockStatus('ready'), 4000);
    }
  };

  if (loading) return <div className="page-loading">Loading attendance records...</div>;

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>Attendance Records</h1>
        <div className="filter-controls">
          <button className="btn btn-secondary" onClick={exportSelected}>Export Selected</button>
          <button className="btn btn-primary" onClick={exportAll}>Export All</button>
        </div>
      </div>

      <div className="fp-station-panel">
        <button
          className="fp-station-toggle"
          onClick={() => setFpStationOpen((o) => !o)}
        >
          <Fingerprint size={16} />
          Fingerprint Station
          <span className={`fp-station-status-dot enabled`} />
          <span style={{ fontSize: 11, color: '#94a3b8' }}>
            {deviceStatus}
          </span>
          {fpStationOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>

        {fpStationOpen && (
          <div className="fp-station-body">
            {fpLastScan && (
              <div className={`fp-last-scan ${fpLastScan.action}`}>
                <strong>
                  {fpLastScan.action === 'clock_in' ? '⬆ Clocked In' : '⬇ Clocked Out'}
                </strong>
                {' — User ID '}{fpLastScan.userId}
                {' at '}{fpLastScan.attendance?.clockIn || fpLastScan.attendance?.clockOut}
              </div>
            )}
            <FingerprintScanner
              status={fpClockStatus === 'idle' ? deviceStatus : fpClockStatus}
              error={fpClockError}
              onScan={handleFingerprintClock}
              disabled={fpClockStatus === 'scanning'}
              compact
              label="Place employee finger on reader to clock in / out"
            />
          </div>
        )}
      </div>

      <div className="users-filters">
        <div className="search-bar" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employees..."
          />
          <select className="filter-select" value={exportPeriod} onChange={(e) => setExportPeriod(e.target.value)}>
            <option value="daily">Daily Export</option>
            <option value="monthly">Monthly Export</option>
          </select>
          <label style={{ color: 'var(--text2)' }}>
            <input
              type="checkbox"
              checked={filteredEmployees.length > 0 && selectedEmployees.length === filteredEmployees.length}
              onChange={(e) => toggleAll(e.target.checked)}
            />{' '}
            Select All
          </label>
        </div>
      </div>

      <div className="users-table-container" style={{ marginBottom: 20 }}>
        <table className="users-table">
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Department</th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map((u) => (
              <tr
                key={u.id}
                onClick={() => {
                  setActiveEmployeeId(String(u.id));
                  setTimeout(() => {
                    calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }, 0);
                }}
                style={{ cursor: 'pointer' }}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedEmployees.includes(u.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      toggleEmployee(u.id);
                    }}
                  />
                </td>
                <td>{`${u.firstName || ''} ${u.lastName || ''}`.trim() || '-'}</td>
                <td>{u.email}</td>
                <td>{u.role}</td>
                <td>{u.department?.name || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="users-filters" style={{ marginTop: 20 }}>
        <div className="filter-controls">
          <select className="filter-select" value={currentMonth} onChange={(e) => setCurrentMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2024, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
          <select className="filter-select" value={currentYear} onChange={(e) => setCurrentYear(Number(e.target.value))}>
            {[2024, 2025, 2026, 2027].map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <select className="filter-select" value={workType} onChange={(e) => setWorkType(e.target.value)}>
            <option value="all">All</option>
            <option value="regular">Regular Work</option>
            <option value="remote">Remote Work</option>
          </select>
        </div>
      </div>

      {/* Attendance Rate Stats for Selected Employee */}
      {activeEmployeeId && (
        <div className="attendance-stats" style={{ marginTop: 12 }}>
          <div className="stat-card">
            <div className="stat-label">Attendance Rate</div>
            <div className="stat-value">
              {(() => {
                // Calculate workdays in the month (excluding weekends)
                const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
                let workdays = 0;
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(currentYear, currentMonth - 1, day);
                  const dayOfWeek = date.getDay();
                  if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Saturday (6) and Sunday (0)
                    workdays++;
                  }
                }

                // Get authorized leave days
                const authorizedLeaveDays = new Set(employeeCalendarRecords.filter(r => isLeaveRecord(r)).map(r => r.date)).size;

                // Total expected workdays = workdays - authorized leaves
                const totalExpectedDays = workdays - authorizedLeaveDays;

                // Present days (deduplicate by date so remote + regular on same day count once)
                const presentLikeDays = new Set(employeeCalendarRecords.filter(r =>
                  !isLeaveRecord(r) && ['present', 'late'].includes(String(r.status).toLowerCase())
                ).map(r => r.date)).size;

                const rate = totalExpectedDays > 0 ? ((presentLikeDays / totalExpectedDays) * 100).toFixed(1) : '0.0';
                return `${rate}%`;
              })()}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Days Present</div>
            <div className="stat-value">
              {new Set(employeeCalendarRecords.filter(r => !isLeaveRecord(r) && ['present', 'late'].includes(String(r.status).toLowerCase())).map(r => r.date)).size}
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Expected Workdays</div>
            <div className="stat-value">
              {(() => {
                const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
                let workdays = 0;
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(currentYear, currentMonth - 1, day);
                  const dayOfWeek = date.getDay();
                  if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                    workdays++;
                  }
                }
                const authorizedLeaveDays = new Set(employeeCalendarRecords.filter(r => isLeaveRecord(r)).map(r => r.date)).size;
                return workdays - authorizedLeaveDays;
              })()}
            </div>
          </div>
        </div>
      )}

      <div ref={calendarRef} className="attendance-calendar" style={{ marginTop: 12 }}>
        <div className="calendar-grid">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="calendar-header-day">{day}</div>
          ))}

          {Array.from({ length: 35 }, (_, i) => {
            const day = i - new Date(currentYear, currentMonth - 1, 1).getDay() + 1;
            const dateStr = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayRecords = employeeCalendarRecords.filter((r) => r.date === dateStr);
            const dayRecord = dayRecords[0];
            const isToday = dateStr === new Date().toISOString().slice(0, 10);
            const isFutureDay = dateStr > new Date().toISOString().slice(0, 10);
            const isValidDay = day > 0 && day <= new Date(currentYear, currentMonth, 0).getDate();
            const hasLeave = dayRecords.some((r) => isLeaveRecord(r));
            const effectiveStatus = hasLeave
              ? 'leave'
              : (dayRecord?.status || (!isFutureDay ? 'absent' : ''));
            const statusClass = effectiveStatus ? `badge-${String(effectiveStatus).toLowerCase()}` : '';

            return (
              <div
                key={i}
                className={`calendar-day ${statusClass} ${isToday ? 'today' : ''} ${!isValidDay ? 'empty' : ''}`}
              >
                {isValidDay && (
                  <>
                    <span className="day-number">{day}</span>
                    {dayRecords.length > 0 && (
                      <div className="day-info">
                        <span className="hours">{getTotalWorkedDurationForDate(dayRecords)}</span>
                        {dayRecords.length > 1 && <span className="session-count-badge">{dayRecords.length} sessions</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="users-table-container" style={{ marginTop: 16 }}>
        <table className="users-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Date</th>
              <th>Clock In</th>
              <th>Clock Out</th>
              <th>Total Worked</th>
              <th>Status</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {employeeRecords.length ? (
              employeeRecords.map((r) => (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{formatDateShort(r.date)}</td>
                  <td>{r.clockIn || '-'}</td>
                  <td>{r.clockOut || '-'}</td>
                  <td>{getWorkedDuration(r)}</td>
                  <td>{r.status}</td>
                  <td>{r.notes || '-'}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', color: 'var(--text2)' }}>
                  Select an employee to view attendance
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceManagement;
